import React from 'react';
import type {FormEventHandler, ReactNode} from 'react';

import {observer} from 'mobx-react-lite';

import {FormContext} from '../contexts';

import {useInternalForm} from './useInternalForm';

import type {FormValue, HandleFormSubmit} from '../sharedTypes/formHelperTypes';

/**
 * The `ComponentProps` interface defines the properties for the `Form` component.
 * This interface specifies the structure of props required to render a form component that provides
 * state management, validation capabilities, and context for child form elements.
 */
interface ComponentProps {
    /**
     * The child components to render within the form element.
     * This can include form fields, buttons, and any other React elements that should be wrapped by the form context.
     * The children will have access to the form state through the FormContext provider.
     */
    children: ReactNode;
    /**
     * The CSS class name to apply to the form element for styling purposes.
     * This optional property allows for custom styling and integration with CSS frameworks.
     * When not provided, defaults to an empty string.
     */
    className?: string;
    /**
     * The unique identifier for the form element used for DOM manipulation and accessibility.
     * This required property ensures proper form identification and enables features like form validation and submission tracking.
     * The ID should be unique within the document to avoid conflicts with other elements.
     */
    id: string;
    /**
     * The callback function triggered when any form field value changes within the form.
     * This optional handler receives the field name and its new value, enabling real-time form state monitoring.
     * It allows parent components to react to form changes and implement custom validation or side effects.
     */
    onChange?(name: string, value: FormValue): void;
    /**
     * The callback function triggered when form validation errors occur during form processing.
     * This optional handler enables custom error handling and user feedback mechanisms.
     * It is called when validation fails or other form-related errors are encountered.
     */
    onError?(): void;
    /**
     * The callback function triggered when the form reset event is fired.
     * This optional handler receives the standard HTML form event and allows for custom reset logic.
     * It enables cleanup operations and custom state management when the form is reset to its initial state.
     */
    onReset?: FormEventHandler<HTMLFormElement>;
    /**
     * The callback function triggered when the form is submitted successfully.
     * This required handler receives the form data and validation results, enabling form processing logic.
     * It is the primary mechanism for handling form submissions and performing actions with the collected data.
     */
    onSubmit: HandleFormSubmit;
    /**
     * The test identifier used for component identification in automated testing frameworks.
     * This optional property enables reliable test targeting using data attributes like data-cy.
     * When not provided, defaults to 'Form' to ensure consistent test identification.
     *
     * @default 'Form'
     */
    testId?: string;
}

/**
 * A form component that provides form state management and validation capabilities.
 * This component wraps form fields and handles form submission, reset, and change events.
 * It uses MobX for reactive state management and provides a FormContext for child components.
 *
 * @param props           The component props.
 * @param props.children  The child components to render within the form.
 * @param props.className The CSS class name to apply to the form element.
 * @param props.id        The unique identifier for the form element.
 * @param props.onChange  The callback function triggered when any form field value changes.
 * @param props.onError   The callback function triggered when form validation errors occur.
 * @param props.onReset   The callback function triggered when the form is reset.
 * @param props.onSubmit  The callback function triggered when the form is submitted.
 * @param props.testId    The test identifier used for testing purposes.
 * @returns A JSX element representing the form with context provider.
 *
 * @example
 * ```tsx
 * <Form
 *   id="user-form"
 *   onSubmit={(data) => console.log(data)}
 *   onChange={(name, value) => console.log(`${name}: ${value}`)}
 * >
 *   <input name="username" />
 *   <button type="submit">Submit</button>
 * </Form>
 * ```
 */
const Form = observer(({
    children,
    className = '',
    id,
    onChange = () => {},
    onError = () => {},
    onReset = () => {},
    onSubmit,
    testId = 'Form'
}: ComponentProps) => {
    const {form, formState, handleReset, handleSubmit} = useInternalForm(id, onChange, onError, onReset, onSubmit);

    return (
        <form
            ref={form}
            className={className}
            data-cy={testId}
            id={id}
            noValidate
            onReset={handleReset}
            onSubmit={handleSubmit}
        >
            <FormContext.Provider value={formState}>
                {children}
            </FormContext.Provider>
        </form>
    );
});

Form.displayName = 'Form';

export {Form};